import {
  buildInternalCheckoutTracking,
  buildTrackedCheckoutUrl,
  directCheckoutHref,
} from "@/features/checkout/tracking";
import { formatSaleMoneyCents } from "@/lib/format/currency";
import type React from "react";
import type { PublicPlanView } from "../types";
import { PaymentPlanComparisonTable } from "./PaymentPlanComparisonTable";
import {
  formatMonthlyEquivalent,
  formatPlanHeadline,
  formatPlanSupport,
  formatPlanTerm,
  normalizeVisiblePlans,
} from "./payment-plan-facts";

type PlanTone = {
  readonly shellClassName: string;
  readonly badgeClassName: string;
  readonly actionClassName: string;
};

export function PaymentRequiredPlanGrid(props: {
  readonly plans: readonly PublicPlanView[];
  readonly displayUrl: string;
}): React.ReactElement | null {
  const visiblePlans = normalizeVisiblePlans(props.plans);
  if (visiblePlans.length === 0) return null;
  const recommendedPlanId =
    visiblePlans.find((plan) => plan.id === "anual")?.id ??
    visiblePlans.at(-1)?.id ??
    null;
  return (
    <div className="grid gap-3">
      {/* #244: o comparativo vem ANTES dos cartões — quem decide compara, quem
          já decidiu rola direto para o botão. Ele não carrega CTA: a compra
          continua sendo só a dos cartões abaixo. */}
      <PaymentPlanComparisonTable plans={visiblePlans} />
      {visiblePlans.map((plan) => (
        <PlanOfferCard
          key={plan.id}
          plan={plan}
          displayUrl={props.displayUrl}
          isRecommended={recommendedPlanId === plan.id}
        />
      ))}
    </div>
  );
}

function PlanOfferCard(props: {
  readonly plan: PublicPlanView;
  readonly displayUrl: string;
  readonly isRecommended: boolean;
}): React.ReactElement {
  const { plan } = props;
  const tone = getPlanTone(plan.id);
  const headline = formatPlanHeadline(plan);
  const monthlyEquivalent = formatMonthlyEquivalent(plan);
  const supportCopy = formatPlanSupport(plan);
  return (
    <article
      className={`rounded-[24px] border px-4 py-4 ${tone.shellClassName}`}
      data-testid={`payment-plan-${plan.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${tone.badgeClassName}`}
            >
              {plan.displayName}
            </span>
            {props.isRecommended ? <RecommendedPill /> : null}
          </div>
          <p className="mt-3 text-sm font-semibold text-foreground">
            {formatSaleMoneyCents(plan.amountCents)}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-foreground/62">
            {headline}
          </p>
        </div>
        <PlanCycleBadge plan={plan} />
      </div>
      <PlanOfferMeta
        supportCopy={supportCopy}
        monthlyEquivalent={monthlyEquivalent}
        tokensIncluded={plan.tokensIncluded}
      />
      <PlanCheckoutButton
        checkoutUrl={loginPlanCheckoutHref(plan, props.displayUrl)}
        actionClassName={tone.actionClassName}
      />
    </article>
  );
}

function RecommendedPill(): React.ReactElement {
  return (
    <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground">
      Melhor escolha
    </span>
  );
}

function PlanCycleBadge(props: {
  readonly plan: PublicPlanView;
}): React.ReactElement {
  return (
    <div className="rounded-[18px] border border-border bg-background/80 px-3 py-2 text-right">
      <div className="text-[10px] uppercase tracking-[0.14em] text-foreground/38">
        Ciclo
      </div>
      <div className="mt-1 text-xs font-semibold text-foreground">
        {formatPlanTerm(props.plan)}
      </div>
    </div>
  );
}

function PlanOfferMeta(props: {
  readonly supportCopy: string;
  readonly monthlyEquivalent: string | null;
  readonly tokensIncluded: number | null;
}): React.ReactElement {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-foreground/56">
      <span>{props.supportCopy}</span>
      {props.monthlyEquivalent ? <MetaSeparator /> : null}
      {props.monthlyEquivalent ? <span>{props.monthlyEquivalent}</span> : null}
      {typeof props.tokensIncluded === "number" && props.tokensIncluded > 0 ? (
        <MetaSeparator />
      ) : null}
      {typeof props.tokensIncluded === "number" && props.tokensIncluded > 0 ? (
        <span>{props.tokensIncluded} tokens</span>
      ) : null}
    </div>
  );
}

function MetaSeparator(): React.ReactElement {
  return <span className="text-foreground/24">{"\u2022"}</span>;
}

function PlanCheckoutButton(props: {
  readonly checkoutUrl: string;
  readonly actionClassName: string;
}): React.ReactElement {
  const openCheckout = (): void => {
    window.open(props.checkoutUrl, "_blank", "noopener,noreferrer");
  };
  return (
    <button
      type="button"
      onClick={openCheckout}
      className={`mt-4 inline-flex w-full items-center justify-center rounded-[18px] border px-3.5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-transform active:scale-[0.99] ${props.actionClassName}`}
    >
      Adquira
    </button>
  );
}

function loginPlanCheckoutHref(plan: PublicPlanView, fallback: string): string {
  const planId = checkoutPlanSlug(plan);
  return (
    directCheckoutHref(plan, "login-payment-plan") ??
    buildTrackedCheckoutUrl(
      fallback,
      buildInternalCheckoutTracking(planId, "login-payment-plan"),
    )
  );
}

function checkoutPlanSlug(plan: PublicPlanView): string {
  const slug = plan.slug.trim();
  if (slug) return slug;
  return plan.id;
}

function getPlanTone(planId: string): PlanTone {
  if (planId === "anual") {
    return {
      shellClassName:
        "border-warning bg-[linear-gradient(180deg,rgba(51,38,11,0.76),rgba(14,10,9,0.96))] shadow-[0_18px_38px_rgba(122,82,13,0.18)]",
      badgeClassName: "border-warning bg-warning/10 text-warning",
      actionClassName:
        "border-warning bg-warning text-primary-foreground shadow-lg",
    };
  }
  if (planId === "semestral") {
    return {
      shellClassName:
        "border-success bg-[linear-gradient(180deg,rgba(10,43,34,0.76),rgba(8,11,14,0.96))] shadow-[0_18px_38px_rgba(10,89,64,0.16)]",
      badgeClassName: "border-success bg-success/10 text-success",
      actionClassName:
        "border-success bg-success text-primary-foreground shadow-lg",
    };
  }
  return {
    shellClassName:
      "border-brand-cyan bg-[linear-gradient(180deg,rgba(8,39,54,0.76),rgba(7,10,16,0.96))] shadow-[0_18px_38px_rgba(9,93,132,0.16)]",
    badgeClassName: "border-brand-cyan bg-brand-cyan/10 text-brand-cyan",
    actionClassName:
      "border-brand-cyan bg-brand-cyan text-primary-foreground shadow-lg",
  };
}
